// Styles helpers for FileUploaderV2
// See .cursor/requirements/file-uploader-v2-plan.md for details

import { SxProps, Theme } from '@mui/material';

import { COLORS } from '@/styles/tokens/colors';

import { DropzoneAppearance } from './FileUploaderV2.types';

export type BackgroundStyleState =
  | 'normal'
  | 'error'
  | 'hover-with-file'
  | 'disabled';

const ERROR_BACKGROUND_COLOR = COLORS.ORANGE[50];
const ERROR_BORDER_COLOR = COLORS.ORANGE[200];

/**
 * Returns an sx object for the dropzone background, based on the current state
 * and color appearance. The error and disabled states take precedence over the
 * appearance so a blue ('accent') dropzone still turns orange on error and gray
 * when disabled.
 */
export function getDropzoneSx(
  state: BackgroundStyleState,
  appearance: DropzoneAppearance = 'default'
): SxProps<Theme> {
  if (state === 'disabled') {
    return {
      backgroundColor: COLORS.GRAY[100],
      borderColor: COLORS.GRAY[300],
      cursor: 'default',
    };
  }

  if (state === 'error') {
    return {
      backgroundColor: ERROR_BACKGROUND_COLOR,
      borderColor: ERROR_BORDER_COLOR,
      '&:hover': {
        borderStyle: 'solid',
      },
    };
  }

  if (appearance === 'accent') {
    return {
      borderColor: COLORS.BLUE[200],
      backgroundColor:
        state === 'hover-with-file' ? COLORS.BLUE[100] : COLORS.BLUE[50],
      '&:hover': {
        borderStyle: 'solid',
        backgroundColor: COLORS.BLUE[100],
      },
    };
  }

  return {
    borderColor: COLORS.GRAY[300],
    // case where we're hovering a file, but not hovering
    backgroundColor: state === 'hover-with-file' ? COLORS.NAVY[50] : 'white',
    '&:hover': {
      borderStyle: 'solid',
      backgroundColor:
        state === 'hover-with-file' ? COLORS.NAVY[50] : COLORS.GRAY[50],
    },
  };
}

export function getDividerStyles(state: BackgroundStyleState): SxProps<Theme> {
  const dividerColor =
    state === 'error' ? COLORS.ORANGE[100] : COLORS.GRAY[200];
  return {
    flexGrow: 1,
    borderColor: dividerColor,
  };
}
